import './TimePicker.scss';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import TextInput from 'fe-design-base/atoms/TextInput';
import AlertText from 'fe-design-base/molecules/AlertText/AlertText';
import useClickOutside from 'hooks/useClickOutside';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import { createKeyPressHandler } from 'util/keybindings';

import { TIMES_5_MIN, TIMES_15_MIN, TIMES_30_MIN } from './constants';
import TimeSelector from './TimeSelector';
import { smartCoerceIntoValid12HourFormat } from './util';
import {
  canBeCoercedInto12HourFormat,
  convertFrom12HourFormat,
  roundTime,
  stripTime,
} from './utils';

const { cx } = cxHelpers('TimePicker');

const INVALID_TIME_ERROR_MESSAGE = toI18n('errors.timepicker.invalid_time');

const TIME_OPTIONS = {
  5: TIMES_5_MIN,
  15: TIMES_15_MIN,
  30: TIMES_30_MIN,
};

const TimePicker = forwardRef(
  (
    {
      name,
      value, // in 24 hour format
      onChange,
      validationError,
      overrideOnFocus,
      selectedShift = '',
      timeToOverride,
      onClear = null,
      onOpen = null,
      interval = 5,
      className,
      disabled,
      iconSize = 'medium',
      endIconName = 'Clear',
      dataTestId,
      startAfterTime,
      isStartTime = false,
    },
    ref
  ) => {
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const selectorRef = useRef(null);

    const timeOptions = TIME_OPTIONS[interval];
    const [isFocused, setIsFocused] = useState(false);

    const [inputValue, setInputValue] = useState(
      value &&
        canBeCoercedInto12HourFormat(value) &&
        smartCoerceIntoValid12HourFormat(value, startAfterTime, isStartTime)
    );

    useEffect(() => {
      if (selectedShift !== '') {
        setInputValue(
          value &&
            canBeCoercedInto12HourFormat(value) &&
            smartCoerceIntoValid12HourFormat(value, startAfterTime, isStartTime)
        );
      }
    }, [selectedShift, value, startAfterTime, isStartTime]);

    const [selectorOpen, setSelectorOpen] = useState(false);
    const selectedValue = useMemo(() => {
      if (inputValue && canBeCoercedInto12HourFormat(inputValue)) {
        const coerced = smartCoerceIntoValid12HourFormat(
          inputValue,
          startAfterTime,
          isStartTime
        );
        const rounded = roundTime(coerced, interval);

        return rounded;
      }
      return undefined;
    }, [inputValue, interval, startAfterTime, isStartTime]);
    const selectedOptionIndex = useMemo(() => {
      const index = timeOptions.findIndex(option => option === selectedValue);

      return index;
    }, [timeOptions, selectedValue]);
    const [dropDownClicked, setDropDownClicked] = useState(false);

    const triggerChangeEvent = useCallback(
      newValue => {
        onChange({
          target: {
            name,
            value: newValue && convertFrom12HourFormat(newValue),
          },
        });
      },
      [name, onChange]
    );

    const handleClick = useCallback(() => {
      if (!disabled) {
        setSelectorOpen(true);
      }
    }, [disabled]);

    const updateSelection = useCallback(
      selectedOption => {
        setInputValue(selectedOption);
        triggerChangeEvent(selectedOption);
      },
      [triggerChangeEvent]
    );

    const handleSelection = useCallback(
      selectedOption => {
        updateSelection(selectedOption);
        setSelectorOpen(false);
        inputRef.current?.focus();
      },
      [updateSelection]
    );

    const isTimeInvalid = useMemo(
      () => inputValue && !canBeCoercedInto12HourFormat(inputValue),
      [inputValue]
    );

    const handleClickOutsideSelector = useCallback(() => {
      setSelectorOpen(false);
    }, [setSelectorOpen]);

    const updateTimeIfValid = useCallback(() => {
      if (inputValue && !isTimeInvalid) {
        const coercedValue = smartCoerceIntoValid12HourFormat(
          stripTime(inputValue),
          startAfterTime,
          isStartTime
        );
        if (inputValue.toLowerCase() !== coercedValue.toLowerCase()) {
          setInputValue(coercedValue);
        }
      }
    }, [inputValue, isTimeInvalid, startAfterTime, isStartTime]);

    const handleEnterKeyDown = useCallback(() => {
      if (selectorOpen) {
        setSelectorOpen(false);
      }
      updateTimeIfValid();
    }, [updateTimeIfValid, selectorOpen]);

    const handleBlur = useCallback(() => {
      updateTimeIfValid();
      if (!dropDownClicked) {
        setTimeout(() => setSelectorOpen(false));
      }
      setIsFocused(false);
    }, [updateTimeIfValid, dropDownClicked]);

    const handleArrowDown = useCallback(
      debounce(() => {
        if (!selectorOpen) {
          return setSelectorOpen(true);
        }
        let newIndex = selectedOptionIndex + 1;
        if (newIndex >= timeOptions.length) newIndex = 0;
        updateSelection(timeOptions[newIndex]);
      }),
      [selectedOptionIndex, selectorOpen, updateSelection]
    );

    const handleArrowUp = useCallback(
      debounce(() => {
        if (!selectorOpen) {
          return setSelectorOpen(true);
        }
        let newIndex = selectedOptionIndex - 1;
        if (newIndex < 0) newIndex = timeOptions.length - 1;
        updateSelection(timeOptions[newIndex]);
      }),
      [selectedOptionIndex, selectorOpen, updateSelection]
    );

    const handleKeyDown = createKeyPressHandler({
      Enter: handleEnterKeyDown,
      ArrowDown: handleArrowDown,
      ArrowUp: handleArrowUp,
    });

    const updateInput = useCallback(
      newValue => {
        setInputValue(newValue);

        if (newValue && canBeCoercedInto12HourFormat(newValue)) {
          const coercedValue = smartCoerceIntoValid12HourFormat(
            stripTime(newValue),
            startAfterTime,
            isStartTime
          );
          if (value?.toLowerCase() !== coercedValue.toLowerCase()) {
            triggerChangeEvent(coercedValue);
          }
        }
      },
      [triggerChangeEvent, value, startAfterTime, isStartTime]
    );

    const handleClear = useCallback(() => {
      setInputValue('');
      triggerChangeEvent('');
      if (onClear) onClear();
    }, [triggerChangeEvent, onClear]);

    const handleOnFocus = useCallback(() => {
      if (!selectorOpen) {
        if (overrideOnFocus) {
          updateInput(timeToOverride);
        }
        if (onOpen) onOpen();
        setSelectorOpen(true);
      }
      setIsFocused(true);
    }, [selectorOpen, overrideOnFocus, onOpen, updateInput, timeToOverride]);

    const handleInputChange = useCallback(
      e => {
        updateInput(e.target.value);
      },
      [updateInput]
    );

    const handleMouseDown = useCallback(() => {
      // Workaround to detect if scrollbar was clicked
      if (!dropDownClicked) setDropDownClicked(true);
    }, [dropDownClicked]);

    const inputProps = useMemo(
      () => ({
        autoComplete: 'off',
      }),
      []
    );

    useClickOutside([containerRef, selectorRef], handleClickOutsideSelector);

    return (
      <Box column ref={ref}>
        <Box
          grow={1}
          relative
          onClick={handleClick}
          ref={containerRef}
          onFocus={handleOnFocus}
          onBlur={handleBlur}
          className={className}
          data-testid={dataTestId}
        >
          <TextInput
            dataTestId={dataTestId}
            className={cx({ error: validationError })}
            name={name}
            value={inputValue}
            placeholder="--:-- --"
            error={!!isTimeInvalid}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            endIcon={
              !disabled && (
                <Box w={30}>
                  {isFocused ? (
                    <Icon
                      size={iconSize}
                      color="purple700"
                      iconName={endIconName}
                      onMouseDown={handleClear}
                      className={cx('clear-icon')}
                    />
                  ) : (
                    <Box w={40} />
                  )}
                </Box>
              )
            }
            onBlur={handleBlur}
            inputProps={inputProps}
            ref={inputRef}
            disabled={disabled}
          />
          {selectorOpen && (
            <TimeSelector
              value={selectedValue}
              onSelect={handleSelection}
              options={timeOptions}
              onMouseDown={handleMouseDown}
              ref={selectorRef}
            />
          )}
        </Box>
        {!!isTimeInvalid && (
          <Box mv={8}>
            <AlertText variant="error" message={INVALID_TIME_ERROR_MESSAGE} />
          </Box>
        )}
      </Box>
    );
  }
);

TimePicker.propTypes = {
  name: PropTypes.string.isRequired,
  initialTime: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  onSelect: PropTypes.func,
  onOpen: PropTypes.func,
  validationError: PropTypes.bool,
  overrideOnFocus: PropTypes.bool,
  interval: PropTypes.number,
  timeToOverride: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  iconSize: PropTypes.string,
  endIconName: PropTypes.string,
  dataTestId: PropTypes.string,
  startAfterTime: PropTypes.string,
  isStartTime: PropTypes.bool,
};

export default TimePicker;
